/**
 * Shared helper for context menus / dropdowns that render with position:fixed
 * over the canvas. Computes the usable on-screen rectangle — the .react-flow
 * viewport, shrunk by whichever of the side panels / footer are currently
 * visible and overlapping it — so a menu can be clamped to stay fully visible
 * instead of flowing out past the canvas edge.
 */

export interface CanvasBounds {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export function getCanvasMenuBounds(referenceEl?: Element | null): CanvasBounds {
  // `referenceEl?.closest('.react-flow')` only works while referenceEl is still a DOM
  // descendant of the canvas. Menus portaled to document.body (so they escape any
  // pan/zoom transform) are detached from that tree, so `closest` would always miss —
  // fall back to a direct document lookup, which finds the (single) canvas regardless.
  const canvasEl = (referenceEl?.closest('.react-flow') as HTMLElement | null)
    ?? (document.querySelector('.react-flow') as HTMLElement | null);
  const canvasRect = canvasEl
    ? canvasEl.getBoundingClientRect()
    : { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  const bounds: CanvasBounds = {
    top: canvasRect.top,
    left: canvasRect.left,
    right: canvasRect.right,
    bottom: canvasRect.bottom,
  };

  // The node palette / properties side panels and the bottom status footer float over
  // the canvas and can visually obscure content that's technically still inside
  // .react-flow's own box — shrink the usable bounds to whatever's actually
  // unobstructed by them when they're visible.
  const leftPanel = document.querySelector('.left-toolbar');
  if (leftPanel) {
    const r = leftPanel.getBoundingClientRect();
    if (r.bottom > bounds.top && r.top < bounds.bottom) bounds.left = Math.max(bounds.left, r.right);
  }
  const rightPanel = document.querySelector('.right-toolbar');
  if (rightPanel) {
    const r = rightPanel.getBoundingClientRect();
    if (r.bottom > bounds.top && r.top < bounds.bottom) bounds.right = Math.min(bounds.right, r.left);
  }
  const footer = document.querySelector('.flow-footer');
  if (footer) {
    const r = footer.getBoundingClientRect();
    if (r.right > bounds.left && r.left < bounds.right) bounds.bottom = Math.min(bounds.bottom, r.top);
  }

  return bounds;
}

/**
 * Clamps a desired top/left so the menu's full rect stays inside bounds.
 * `margin` insets the usable area a bit further — these menus render with a soft
 * glow/shadow (blur radius up to ~30px) that bleeds past the box measured by
 * getBoundingClientRect, so clamping to the exact edge still lets that glow spill
 * off-screen and read as overflow even though the box itself is inside bounds.
 */
export function clampMenuPosition(
  desired: { top: number; left: number },
  menuSize: { width: number; height: number },
  bounds: CanvasBounds,
  margin = 12
): { top: number; left: number } {
  const left0 = bounds.left + margin;
  const right0 = bounds.right - margin;
  const top0 = bounds.top + margin;
  const bottom0 = bounds.bottom - margin;
  const maxLeft = Math.max(left0, right0 - menuSize.width);
  const maxTop = Math.max(top0, bottom0 - menuSize.height);
  return {
    left: Math.min(Math.max(desired.left, left0), maxLeft),
    top: Math.min(Math.max(desired.top, top0), maxTop),
  };
}
